/**
 * Fiduciary Schema — Canonical Model Builder
 *
 * Converts external data (import JSON v1.0/v1.1 or KeyPeopleManager UI)
 * into a single canonical internal model used for validation, rendering,
 * and document generation.
 *
 * Canonical model structure:
 *   people        — registry of unique persons, keyed by generated ID
 *   roleAssignments — role → { tiers: [{ people: [id, ...] }] }
 *   packageConfig — package_type, has_minor_children, survivorship_direction
 *   trustConfig   — age thresholds for trust governance
 */

import {
  FIDUCIARY_ROLES,
  getRolesForPackage,
  isRoleAllowed,
  getMaxSuccessorDepth,
} from './packageCapabilities.js';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_TRUST_CONFIG = {
  beneficiary_cotrust_age: 25,
  beneficiary_sole_trust_age: 30,
  trust_protector_age: 30,
  pot_trust_termination_age: 22,
};

// Matter-level facts that drive conditional blocks in the post-execution
// To-Do List (and may be consumed by other templates in the future).
// All keys default to false — a matter with no facts supplied is treated as
// having none of these situations.
const DEFAULT_MATTER_FACTS = {
  has_life_insurance: false,
  has_ilit: false,
  has_retirement_accounts: false,
  has_pension_plans: false,
  has_joint_accounts: false,
  has_closely_held_business: false,
  has_real_estate_to_retitle: false,
  has_memoranda_tangible_property: false,
  has_digital_assets_inventory: false,
  has_letter_of_wishes: false,
};

function coerceMatterFacts(input) {
  if (!input || typeof input !== 'object') return { ...DEFAULT_MATTER_FACTS };
  const out = { ...DEFAULT_MATTER_FACTS };
  for (const key of Object.keys(DEFAULT_MATTER_FACTS)) {
    if (input[key] !== undefined) out[key] = Boolean(input[key]);
  }
  return out;
}

// ---------------------------------------------------------------------------
// People Registry Helpers
// ---------------------------------------------------------------------------

/**
 * Generate a stable person ID from name string.
 * Two people with the same name (case-insensitive, trimmed) get the same ID.
 */
function personKey(name) {
  return name.trim().toLowerCase().replace(/\s+/g, '_');
}

/**
 * Add a person to the registry if not already present.
 * If already present, merge relationship if the existing entry lacks one.
 * @returns {string} The person's ID in the registry
 */
function registerPerson(people, name, relationship) {
  const trimmedName = (name || '').trim();
  if (!trimmedName) return null;

  const key = personKey(trimmedName);
  if (!people[key]) {
    people[key] = {
      name: trimmedName,
      relationship: (relationship || '').trim() || null,
    };
  } else if (!people[key].relationship && relationship) {
    people[key].relationship = (relationship || '').trim();
  }
  return key;
}

/**
 * Register a person with full contact info (for healthcare agents).
 */
function registerPersonWithContact(people, person) {
  const key = registerPerson(people, person.name, person.relationship);
  if (key && people[key]) {
    if (person.street_address) people[key].street_address = person.street_address;
    if (person.city_state_zip) people[key].city_state_zip = person.city_state_zip;
    if (person.telephone) people[key].telephone = person.telephone;
  }
  return key;
}

// ---------------------------------------------------------------------------
// Import JSON Normalization (v1.0 and v1.1)
// ---------------------------------------------------------------------------

/**
 * Normalize a fiduciary tier from import JSON.
 * v1.0 format: { name, relationship }  (single person, no wrapper)
 * v1.1 format: { people: [{ name, relationship }, ...] }
 *
 * @returns {{ people: string[] }} Tier with person IDs
 */
function normalizeTier(tierData, peopleRegistry) {
  // v1.1 format: has explicit "people" array
  if (tierData.people && Array.isArray(tierData.people)) {
    const ids = tierData.people
      .map(p => registerPerson(peopleRegistry, p.name, p.relationship))
      .filter(Boolean);
    return { people: ids };
  }

  // v1.0 format: tier item IS the person
  if (tierData.name) {
    const id = registerPerson(peopleRegistry, tierData.name, tierData.relationship);
    return id ? { people: [id] } : { people: [] };
  }

  return { people: [] };
}

/**
 * Normalize a healthcare agent tier (always single person, has contact info).
 */
function normalizeHealthcareTier(agentData, peopleRegistry) {
  const id = registerPersonWithContact(peopleRegistry, agentData);
  return id ? { people: [id] } : { people: [] };
}

/**
 * Normalize an array of tiers from import JSON, respecting max depth.
 */
function normalizeTierArray(tiersArray, peopleRegistry, maxDepth) {
  if (!Array.isArray(tiersArray)) return [];
  return tiersArray
    .slice(0, maxDepth)
    .map(t => normalizeTier(t, peopleRegistry))
    .filter(t => t.people.length > 0);
}

/**
 * Build canonical model from estate-plan-import JSON (v1.0 or v1.1).
 *
 * @param {object} importData - Parsed import JSON
 * @returns {object} Canonical model { people, roleAssignments, packageConfig, trustConfig }
 */
/**
 * Build a roleAssignments object from a single fiduciaries-shaped input.
 *
 * Extracted so we can apply the same logic to both `importData.fiduciaries`
 * (the client's picks / joint plan picks) and `importData.spouse_fiduciaries`
 * (the spouse's per-role overrides for asymmetric married plans). The
 * `people` registry is shared between the two calls so a person who appears
 * in both objects gets a single canonical ID.
 *
 * @param {object} fid - A fiduciaries-shaped object (may be undefined/null/{})
 * @param {object} people - Shared people registry, mutated in place
 * @returns {object} roleAssignments keyed by FIDUCIARY_ROLES
 */
function buildRoleAssignmentsFromFiduciaries(fid, people) {
  const safeFid = fid || {};
  const roleAssignments = {};

  // Executor: primary + successors
  const executorTiers = [];
  if (safeFid.executor) {
    const id = registerPerson(people, safeFid.executor.name, safeFid.executor.relationship);
    if (id) executorTiers.push({ people: [id] });
  }
  if (safeFid.successor_executors) {
    const successors = normalizeTierArray(
      safeFid.successor_executors, people, getMaxSuccessorDepth('executor')
    );
    executorTiers.push(...successors);
  }
  roleAssignments.executor = { tiers: executorTiers };

  // Trustee: client is implicit initial, successors are the tiers
  const trusteeTiers = normalizeTierArray(
    safeFid.successor_trustees, people, getMaxSuccessorDepth('trustee')
  );
  roleAssignments.trustee = { tiers: trusteeTiers };

  // Trust Protector: successors only
  const protectorTiers = normalizeTierArray(
    safeFid.trust_protectors, people, getMaxSuccessorDepth('trust_protector')
  );
  roleAssignments.trust_protector = { tiers: protectorTiers };

  // Estate Protector: single appointment, no successors
  const epTiers = [];
  if (safeFid.estate_protector) {
    const id = registerPerson(people, safeFid.estate_protector.name, null);
    if (id) epTiers.push({ people: [id] });
  }
  roleAssignments.estate_protector = { tiers: epTiers };

  // Guardian: primary + successors (conditional on minor children)
  const guardianTiers = [];
  if (safeFid.guardians && Array.isArray(safeFid.guardians)) {
    const maxGuardianTiers = 1 + getMaxSuccessorDepth('guardian'); // primary + successors
    const normalized = safeFid.guardians
      .slice(0, maxGuardianTiers)
      .map(g => normalizeTier(g, people))
      .filter(t => t.people.length > 0);
    guardianTiers.push(...normalized);
  }
  roleAssignments.guardian = { tiers: guardianTiers };

  // Financial Agent: primary + successor. Each tier may carry one person
  // (namedRole shape) or two people (fiduciaryTier shape — co-agents acting
  // unanimously). normalizeTier handles both v1.0 flat-namedRole and v1.1+
  // fiduciaryTier shapes identically.
  const faTiers = [];
  if (safeFid.financial_agent) {
    const tier = normalizeTier(safeFid.financial_agent, people);
    if (tier.people.length > 0) faTiers.push(tier);
  }
  if (safeFid.successor_financial_agent) {
    const tier = normalizeTier(safeFid.successor_financial_agent, people);
    if (tier.people.length > 0) faTiers.push(tier);
  }
  roleAssignments.financial_agent = { tiers: faTiers };

  // Healthcare Agent: primary + up to 2 successors
  const hcTiers = [];
  if (safeFid.healthcare_agents && Array.isArray(safeFid.healthcare_agents)) {
    const maxHcTiers = 1 + getMaxSuccessorDepth('healthcare_agent');
    safeFid.healthcare_agents.slice(0, maxHcTiers).forEach(agent => {
      const tier = normalizeHealthcareTier(agent, people);
      if (tier.people.length > 0) hcTiers.push(tier);
    });
  }
  roleAssignments.healthcare_agent = { tiers: hcTiers };

  return roleAssignments;
}

export function normalizeFromImport(importData) {
  const people = {};
  const packageType = importData.package_candidate || null;

  // --- Role Assignments ---
  // Client (or joint) picks always populate roleAssignments. spouse_fiduciaries
  // is optional and only emitted by upstream when at least one role differs
  // from the client's. When present, it produces a parallel
  // spouseRoleAssignments object; the renderer compares the two per-role to
  // decide between joint and split rows.
  const roleAssignments = buildRoleAssignmentsFromFiduciaries(importData.fiduciaries, people);
  const spouseRoleAssignments = importData.spouse_fiduciaries
    ? buildRoleAssignmentsFromFiduciaries(importData.spouse_fiduciaries, people)
    : null;

  // --- Derive has_minor_children ---
  const children = importData.children || [];
  let hasMinorChildren = false;
  if (children.length > 0) {
    if (children.some(c => c.is_minor === true)) {
      hasMinorChildren = true;
    } else if (children.every(c => c.is_minor === false)) {
      hasMinorChildren = false;
    } else {
      hasMinorChildren = null; // unknown — needs attorney confirmation
    }
  }

  // --- Package Config ---
  const packageConfig = {
    package_type: packageType,
    has_minor_children: hasMinorChildren,
    survivorship_direction: importData.survivorship_direction || 'spouse_survives',
    trust_instrument_type: importData.trust_instrument_type === 'amended_and_restated'
      ? 'amended_and_restated'
      : 'original',
  };

  // --- Trust Config (with defaults) ---
  const importTrustConfig = importData.trust_config || {};
  const trustConfig = {
    beneficiary_cotrust_age:
      importTrustConfig.beneficiary_cotrust_age ?? DEFAULT_TRUST_CONFIG.beneficiary_cotrust_age,
    beneficiary_sole_trust_age:
      importTrustConfig.beneficiary_sole_trust_age ?? DEFAULT_TRUST_CONFIG.beneficiary_sole_trust_age,
    trust_protector_age:
      importTrustConfig.trust_protector_age ?? DEFAULT_TRUST_CONFIG.trust_protector_age,
    pot_trust_termination_age: hasMinorChildren === true
      ? (importTrustConfig.pot_trust_termination_age ?? DEFAULT_TRUST_CONFIG.pot_trust_termination_age)
      : null,
  };

  // --- Client/Spouse/Children (pass through for render) ---
  // preferred_first_name: optional informal conversational form of the first
  // name (e.g., "Addie" for "Adelaide"; "Bill" for "William"). Used for
  // greetings only. Full name remains the source of truth for formal
  // identity and signature blocks.
  const clientInfo = {
    full_name: importData.client?.full_name || '',
    preferred_first_name: importData.client?.preferred_first_name || '',
    county_of_residence: importData.client?.county_of_residence || '',
    marital_status: importData.client?.marital_status || null,
    date_of_birth: importData.client?.date_of_birth || '',
    street_address: importData.client?.street_address || '',
    city_state_zip: importData.client?.city_state_zip || '',
  };

  const spouseInfo = importData.spouse
    ? {
        full_name: importData.spouse.full_name || '',
        preferred_first_name: importData.spouse.preferred_first_name || '',
      }
    : null;

  const childrenInfo = children.map(c => ({
    full_name: c.full_name,
    is_minor: c.is_minor,
  }));

  const dates = {
    signing_date: importData.dates?.signing_date || '',
    rlt_date: importData.dates?.rlt_date || '',
  };

  // --- Specific Gifts (live in the Trust — pour-over Will sends all assets
  // to the Trust, so every specific gift is drafted in the Trust instrument) ---
  //
  // `kind` selects the Trust article where the gift appears:
  //   'tpp'           — tangible personal property; rendered inline in Article V
  //                     (Tangible Personal Property), after the Memorandum of
  //                     Wishes, before the General Gift of Tangible Personal
  //                     Property
  //   'pre_residuary' — cash bequest or real-property gift; rendered in a new
  //                     "Pre-Residuary Gifts" article, placed after Article V
  //                     and before the residuary/formula articles
  //
  // `offset_against_residuary` flags hotchpot treatment — the gift's value is
  // charged against the recipient's share of the residue. In V1 the flag is
  // informational for ancillary artifacts; the template prose handles the
  // actual residuary-share math.
  const specificGifts = Array.isArray(importData.specific_gifts)
    ? importData.specific_gifts
        .map(g => {
          const recipientId = registerPerson(
            people,
            g.recipient?.name,
            g.recipient?.relationship
          );
          if (!recipientId || !g.description) return null;
          const kind = g.kind === 'tpp' ? 'tpp' : 'pre_residuary';
          // `in_trust` only meaningful for pre_residuary gifts — when true,
          // the gift is distributed into the recipient's Descendants'
          // Separate Trust rather than outright. Silently ignored for tpp.
          const inTrust = kind === 'pre_residuary' && g.in_trust === true;
          return {
            recipient_id: recipientId,
            description: String(g.description).trim(),
            offset_against_residuary: g.offset_against_residuary === true,
            in_trust: inTrust,
            kind,
          };
        })
        .filter(Boolean)
    : [];

  // --- Matter Facts (conditional drivers for To-Do List + future templates) ---
  const matterFacts = coerceMatterFacts(importData.matter_facts);
  const spouseMatterFacts = importData.spouse_matter_facts
    ? coerceMatterFacts(importData.spouse_matter_facts)
    : null;

  // --- Firm Settings (per-matter override of firm-wide defaults) ---
  // Only stores fields the caller explicitly set; render layer merges over
  // firm-wide defaults.
  const firmSettings = importData.firm_settings && typeof importData.firm_settings === 'object'
    ? { ...importData.firm_settings }
    : null;

  // --- Ambiguities + required confirmations pass-through ---
  // Carry both arrays onto the canonical model so downstream consumers
  // (validation warnings, attorney summary, engagement letter open-items)
  // can reference them. Shape preserved verbatim from the import JSON;
  // schema validation has already ensured each entry's required keys exist.
  const ambiguities = Array.isArray(importData.ambiguities)
    ? importData.ambiguities
    : [];
  const requiredConfirmations = Array.isArray(importData.required_confirmations)
    ? importData.required_confirmations
    : [];

  const planningConfirmation = importData.planning_confirmation
    && typeof importData.planning_confirmation === 'object'
    ? {
        plan_assumptions_text: importData.planning_confirmation.plan_assumptions_text || '',
        engagement_timeline_text: importData.planning_confirmation.engagement_timeline_text || '',
      }
    : null;

  return {
    people,
    roleAssignments,
    spouseRoleAssignments,
    packageConfig,
    trustConfig,
    client: clientInfo,
    spouse: spouseInfo,
    children: childrenInfo,
    dates,
    specific_gifts: specificGifts,
    matterFacts,
    spouseMatterFacts,
    firmSettings,
    ambiguities,
    required_confirmations: requiredConfirmations,
    planning_confirmation: planningConfirmation,
  };
}

// ---------------------------------------------------------------------------
// UI Normalization (from KeyPeopleManager)
// ---------------------------------------------------------------------------

/**
 * Build canonical model from KeyPeopleManager UI state.
 *
 * @param {object[]} uiPeople - Array of person objects from KeyPeopleManager
 *   Each: { id, name, relationship, assignedRoles, street_address?, city_state_zip?, telephone? }
 * @param {object} gatewayAnswers - Gateway question answers
 *   { has_minor_children, survivorship_direction, ...age thresholds }
 * @param {object} clientData - Client/spouse/children/dates from other form sections
 * @param {string} packageType - Selected package type
 * @returns {object} Canonical model
 */
export function normalizeFromUI(uiPeople, gatewayAnswers, clientData, packageType) {
  const people = {};
  const roleAssignments = {};

  // Build people registry from UI entries
  const uiIdToCanonicalId = {};
  for (const person of uiPeople) {
    const canonicalId = registerPerson(people, person.name, person.relationship);
    if (canonicalId) {
      uiIdToCanonicalId[person.id] = canonicalId;
      // Merge contact info if present
      if (person.street_address) people[canonicalId].street_address = person.street_address;
      if (person.city_state_zip) people[canonicalId].city_state_zip = person.city_state_zip;
      if (person.telephone) people[canonicalId].telephone = person.telephone;
    }
  }

  // Build role assignments from assignedRoles on each person
  // The UI uses role IDs like 'executor', 'successor_executor', 'second_successor_executor', etc.
  // We need to map these to our canonical role/tier structure.
  const roleTierMap = buildRoleTierMap(uiPeople, uiIdToCanonicalId);

  // Convert to canonical roleAssignments
  for (const roleId of Object.keys(FIDUCIARY_ROLES)) {
    const tiers = roleTierMap[roleId] || [];
    roleAssignments[roleId] = { tiers };
  }

  const packageConfig = {
    package_type: packageType,
    has_minor_children: gatewayAnswers.has_minor_children ?? false,
    survivorship_direction: gatewayAnswers.survivorship_direction || 'spouse_survives',
    trust_instrument_type: gatewayAnswers.trust_instrument_type === 'amended_and_restated'
      ? 'amended_and_restated'
      : 'original',
  };

  const trustConfig = {
    beneficiary_cotrust_age:
      gatewayAnswers.beneficiary_cotrust_age ?? DEFAULT_TRUST_CONFIG.beneficiary_cotrust_age,
    beneficiary_sole_trust_age:
      gatewayAnswers.beneficiary_sole_trust_age ?? DEFAULT_TRUST_CONFIG.beneficiary_sole_trust_age,
    trust_protector_age:
      gatewayAnswers.trust_protector_age ?? DEFAULT_TRUST_CONFIG.trust_protector_age,
    pot_trust_termination_age: (gatewayAnswers.has_minor_children ?? false) === true
      ? (gatewayAnswers.pot_trust_termination_age ?? DEFAULT_TRUST_CONFIG.pot_trust_termination_age)
      : null,
  };

  return {
    people,
    roleAssignments,
    packageConfig,
    trustConfig,
    client: clientData?.client || {
      full_name: '', county_of_residence: '', marital_status: null, date_of_birth: '',
      street_address: '', city_state_zip: '',
    },
    spouse: clientData?.spouse || null,
    children: clientData?.children || [],
    dates: clientData?.dates || { signing_date: '', rlt_date: '' },
    specific_gifts: clientData?.specific_gifts || [],
    matterFacts: coerceMatterFacts(clientData?.matterFacts),
    spouseMatterFacts: clientData?.spouseMatterFacts
      ? coerceMatterFacts(clientData.spouseMatterFacts)
      : null,
    firmSettings: clientData?.firmSettings && typeof clientData.firmSettings === 'object'
      ? { ...clientData.firmSettings }
      : null,
  };
}

// ---------------------------------------------------------------------------
// UI Role Mapping
// ---------------------------------------------------------------------------

/**
 * Maps UI role IDs (e.g., 'successor_executor', 'second_successor_trustee')
 * to canonical role/tier structure.
 *
 * UI role IDs follow patterns like:
 *   executor, successor_executor, second_successor_executor
 *   successor_trustee, second_successor_trustee, third_successor_trustee
 *   guardian, successor_guardian
 *   first_successor_trust_protector, second_successor_trust_protector, third_successor_trust_protector
 *   financial_agent (mapped as agent), successor_financial_agent
 *   healthcare_agent, first_successor_healthcare_agent, second_successor_healthcare_agent
 */

const UI_ROLE_TO_CANONICAL = {
  // Executor
  executor:                    { role: 'executor', tier: 0 },
  successor_executor:          { role: 'executor', tier: 1 },
  second_successor_executor:   { role: 'executor', tier: 2 },

  // Trustee (client is implicit tier 0, named successors start at tier 0 in canonical)
  successor_trustee:           { role: 'trustee', tier: 0 },
  second_successor_trustee:    { role: 'trustee', tier: 1 },
  third_successor_trustee:     { role: 'trustee', tier: 2 },

  // Trust Protector
  first_successor_trust_protector:  { role: 'trust_protector', tier: 0 },
  second_successor_trust_protector: { role: 'trust_protector', tier: 1 },
  third_successor_trust_protector:  { role: 'trust_protector', tier: 2 },
  trust_protector:                  { role: 'trust_protector', tier: 0 },

  // Estate Protector
  estate_protector:            { role: 'estate_protector', tier: 0 },

  // Guardian
  guardian:                    { role: 'guardian', tier: 0 },
  successor_guardian:          { role: 'guardian', tier: 1 },

  // Financial Agent
  financial_agent:             { role: 'financial_agent', tier: 0 },
  successor_financial_agent:   { role: 'financial_agent', tier: 1 },

  // Healthcare Agent
  healthcare_agent:                      { role: 'healthcare_agent', tier: 0 },
  first_successor_healthcare_agent:      { role: 'healthcare_agent', tier: 1 },
  second_successor_healthcare_agent:     { role: 'healthcare_agent', tier: 2 },
};

/**
 * Build a role → tiers map from UI people with assigned roles.
 */
function buildRoleTierMap(uiPeople, uiIdToCanonicalId) {
  // Collect: { role: { tierIndex: [canonicalId, ...] } }
  const intermediate = {};

  for (const person of uiPeople) {
    const canonicalId = uiIdToCanonicalId[person.id];
    if (!canonicalId) continue;

    for (const uiRoleId of (person.assignedRoles || [])) {
      const mapping = UI_ROLE_TO_CANONICAL[uiRoleId];
      if (!mapping) continue;

      if (!intermediate[mapping.role]) intermediate[mapping.role] = {};
      if (!intermediate[mapping.role][mapping.tier]) intermediate[mapping.role][mapping.tier] = [];
      intermediate[mapping.role][mapping.tier].push(canonicalId);
    }
  }

  // Convert to array of tiers, sorted by tier index, no gaps
  const result = {};
  for (const roleId of Object.keys(intermediate)) {
    const tierIndices = Object.keys(intermediate[roleId]).map(Number).sort((a, b) => a - b);
    result[roleId] = tierIndices.map(idx => ({
      people: intermediate[roleId][idx],
    }));
  }

  return result;
}

// ---------------------------------------------------------------------------
// Model Inspection Helpers
// ---------------------------------------------------------------------------

/**
 * Get the successor count for a role in the canonical model.
 * For roles with an initial appointment (executor, guardian), tier 0 is primary,
 * so successor count = tiers.length - 1.
 * For roles where client is implicit initial (trustee), all tiers are successors.
 */
export function getSuccessorCount(model, roleId) {
  const assignment = model.roleAssignments[roleId];
  if (!assignment || !assignment.tiers) return 0;

  const roleDef = FIDUCIARY_ROLES[roleId];
  if (!roleDef) return 0;

  if (roleDef.hasInitialAppointment) {
    // Tier 0 = primary, rest are successors
    return Math.max(0, assignment.tiers.length - 1);
  }
  // All tiers are successors (trustee, trust_protector)
  return assignment.tiers.length;
}

/**
 * Check if a specific tier of a role is a pair (co-fiduciary).
 */
export function isTierPair(model, roleId, tierIndex) {
  const tiers = model.roleAssignments[roleId]?.tiers || [];
  if (tierIndex >= tiers.length) return false;
  return tiers[tierIndex].people.length === 2;
}

/**
 * Check if any tier of a role is a pair.
 */
export function hasAnyPair(model, roleId) {
  const tiers = model.roleAssignments[roleId]?.tiers || [];
  return tiers.some(t => t.people.length === 2);
}

export { DEFAULT_TRUST_CONFIG, DEFAULT_MATTER_FACTS, UI_ROLE_TO_CANONICAL };
